/**
 * Pantalla "Editar orden" (`GET` + `PATCH /api/v1/orders/<id>`). Reemplaza el
 * placeholder de `ROUTES.ordersEdit`. Equivale a `edit_order.html` del SSR.
 *
 * Exige `edit_orders` (mismo permiso que la API y el menú). Carga el detalle con
 * `useOrder` (TanStack Query) y respeta los **permisos granulares por campo**
 * (`FIELD_EDIT_PERMISSION`): cada campo es editable solo si el usuario tiene su
 * permiso; los demás se pintan solo-lectura y **no se envían** (el `OrderForm`
 * omite del cuerpo lo no editable). En éxito navega a la lista de pendientes.
 *
 * El `order_type` se ofrece si la sede permite láser (`sedeLaserActivo`) o si la
 * orden ya es de tipo `laser` (para no ocultar/cambiar inadvertidamente su tipo).
 * Estados de carga/error contemplados (sin pantallas en blanco). Presentacional +
 * hooks; sin `fetch` (la sede va por `X-Sede-Id` en lib/api).
 */
import { useNavigate, useParams } from "react-router-dom";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { useAuth } from "@/lib/auth";
import { ROUTES } from "@/features/navigation";

import { FIELD_EDIT_PERMISSION } from "../constants";
import { dateInputToApi, isoToDateInput } from "../format";
import { useOrder } from "../hooks/useOrder";
import { useUpdateOrder } from "../hooks/useUpdateOrder";
import type { OrderFormValues } from "../schemas";
import type { OrderDetail, OrderWriteBody, PaymentStatus } from "../types";
import { OrderForm } from "./OrderForm";

/** Permiso que gobierna la edición de órdenes (igual que el menú y la API). */
const EDIT_ORDERS_PERMISSION = "edit_orders";

/** Mapea el detalle de la orden a los valores iniciales del formulario (UI). */
function detailToFormValues(order: OrderDetail): OrderFormValues {
  return {
    order_number: order.order_number,
    customer_name: order.customer_name,
    attendant: order.attendant,
    sale_amount: String(order.sale_amount ?? ""),
    invoice_date: isoToDateInput(order.invoice_date),
    delivery_date: isoToDateInput(order.delivery_date),
    delivery_time: order.delivery_time ?? "",
    payment_status: order.payment_status,
    amount_paid: order.amount_paid ? String(order.amount_paid) : "",
    description: order.description ?? "",
    order_type: order.order_type === "laser" ? "laser" : "normal",
  };
}

/**
 * Construye el cuerpo del PATCH desde los campos editables (ya filtrados por el
 * `OrderForm` según permiso granular). Convierte las fechas a `DD/MM/YYYY` solo si
 * están presentes; un campo ausente se omite (semántica parcial de PATCH).
 */
function toPatchBody(values: Partial<OrderFormValues>): Partial<OrderWriteBody> {
  const body: Partial<OrderWriteBody> = {};
  if (values.order_number !== undefined) body.order_number = values.order_number;
  if (values.customer_name !== undefined) body.customer_name = values.customer_name;
  if (values.attendant !== undefined) body.attendant = values.attendant;
  if (values.sale_amount !== undefined) body.sale_amount = values.sale_amount;
  if (values.invoice_date !== undefined) {
    body.invoice_date = dateInputToApi(values.invoice_date);
  }
  if (values.delivery_date !== undefined) {
    body.delivery_date = dateInputToApi(values.delivery_date);
  }
  if (values.delivery_time !== undefined) body.delivery_time = values.delivery_time;
  if (values.payment_status !== undefined) {
    body.payment_status = values.payment_status as PaymentStatus;
  }
  if (values.amount_paid !== undefined) body.amount_paid = values.amount_paid;
  if (values.description !== undefined) body.description = values.description;
  if (values.order_type !== undefined) body.order_type = values.order_type;
  return body;
}

export function OrderEditScreen() {
  const navigate = useNavigate();
  const params = useParams();
  const { hasPermission, sedeLaserActivo } = useAuth();
  const orderId = Number(params.orderId);
  const validId = Number.isInteger(orderId) && orderId > 0;

  const { data, isPending, isError, error } = useOrder(validId ? orderId : null);
  const updateOrder = useUpdateOrder();

  if (!hasPermission(EDIT_ORDERS_PERMISSION)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Editar orden</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600" role="alert">
            No tienes permiso para editar órdenes.
          </p>
        </CardContent>
      </Card>
    );
  }

  function handleSubmit(editableValues: Partial<OrderFormValues>) {
    if (!data) {
      return;
    }
    updateOrder.mutate(
      { id: data.id, body: toPatchBody(editableValues) },
      {
        onSuccess: () => {
          navigate(ROUTES.ordersPending);
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold text-slate-900">
        {data ? `Editar orden ${data.order_number}` : "Editar orden"}
      </h1>
      <Card>
        <CardContent>
          {!validId ? (
            <p className="text-sm text-red-600" role="alert">
              Orden no válida.
            </p>
          ) : isPending ? (
            <p className="text-sm text-slate-500" role="status">
              Cargando orden…
            </p>
          ) : isError ? (
            <p className="text-sm text-red-600" role="alert">
              {error instanceof Error && error.message
                ? error.message
                : "No se pudo cargar la orden."}
            </p>
          ) : (
            <OrderForm
              mode="edit"
              initialValues={detailToFormValues(data)}
              canEditField={(field) => {
                if (field === "order_type") {
                  // Sin permiso granular: solo `edit_orders` (ya verificado) + láser.
                  return sedeLaserActivo || data.order_type === "laser";
                }
                if (field === "amount_paid") {
                  // El abono se gobierna con el permiso del estado de pago.
                  return hasPermission(FIELD_EDIT_PERMISSION.payment_status);
                }
                const permission = FIELD_EDIT_PERMISSION[field];
                return permission ? hasPermission(permission) : false;
              }}
              showOrderType={sedeLaserActivo || data.order_type === "laser"}
              isSubmitting={updateOrder.isPending}
              apiError={updateOrder.isError ? updateOrder.error : null}
              onSubmit={handleSubmit}
              submitLabel="Actualizar"
              onCancel={() => navigate(ROUTES.ordersPending)}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
